import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFormType } from '@neo/blocks/forms/types'
import { Label } from '@neo/primitives/label'
import { Tooltip } from '@neo/primitives/tooltip'
import { Info } from 'lucide-react'
import React from 'react'
import { userIdleConfigSchema } from '../../config/agent-settings.validation.config'
import type { AgentSettingsFormValues } from '../../config/base.config'

export const UserIdleConfig = React.memo(
  ({ form }: { form: GenericFormType<AgentSettingsFormValues> }) => (
    <div className="grid grid-cols-2 gap-4">
      <FieldsRenderer
        fields={[
          {
            name: 'voice_ai_config.user_idle_config.reminder_msg',
            componentType: 'textarea',
            className: 'col-span-2',
            zodValidation: userIdleConfigSchema.shape.reminder_msg,
            inputProps: {
              label: 'Reminder message',
              className: 'bg-background',
              placeholder: 'Enter a message to remind the user to interact',
            },
          },
          {
            name: 'voice_ai_config.user_idle_config.hangup_msg',
            componentType: 'textarea',
            className: 'col-span-2',
            zodValidation: userIdleConfigSchema.shape.hangup_msg,
            inputProps: {
              className: 'bg-background',
              label: 'Call hangup message',
              placeholder: 'Enter a message to read when the call is disconnected',
            },
          },
          {
            name: 'voice_ai_config.user_idle_config.idling_timeout',
            componentType: 'inputNumber',
            className: 'col-span-1',
            zodValidation: userIdleConfigSchema.shape.idling_timeout,
            eventDataExtractor: (event) => Number(event.target.value) || 0,
            inputProps: {
              label: (
                <Label>
                  <span className="flex items-center gap-1">
                    No response timeout (seconds)
                    <Tooltip content="How long to wait when the user stops speaking">
                      <Info size={12} className="text-secondary" />
                    </Tooltip>
                  </span>
                </Label>
              ),
              type: 'number',
              placeholder: 'Enter the idle timeout in seconds',
            },
          },
          {
            name: 'voice_ai_config.user_idle_config.hold_timeout',
            componentType: 'inputNumber',
            className: 'col-span-1',
            zodValidation: userIdleConfigSchema.shape.hold_timeout,
            onChangeListenTo: ['voice_ai_config.user_idle_config.idling_timeout'],
            getDynamicProps: (state) => ({
              zodVal: userIdleConfigSchema.shape.hold_timeout.refine(
                (val) => val >= (state.voice_ai_config?.user_idle_config?.idling_timeout || 0),
                {
                  message:
                    'Extended Wait Time must be greater than or equal to No Response Timeout',
                },
              ),
            }),
            eventDataExtractor: (event) => Number(event.target.value) || 0,
            inputProps: {
              label: (
                <Label>
                  <span className="flex items-center gap-1">
                    Extended wait time (seconds)
                    <Tooltip content="How long to wait when user says 'wait', 'hold on', or 'give me a moment'">
                      <Info size={12} className="text-secondary" />
                    </Tooltip>
                  </span>
                </Label>
              ),
              type: 'number',
              placeholder: 'Enter the extended wait time in seconds',
            },
          },
          {
            name: 'voice_ai_config.user_idle_config.retries',
            componentType: 'inputNumber',
            zodValidation: userIdleConfigSchema.shape.retries,
            eventDataExtractor: (event) => Number(event.target.value) || 0,
            inputProps: {
              label: (
                <Label>
                  <span className="flex items-center gap-1">
                    Retry
                    <Tooltip content="How many times to try the reminder message before hanging up">
                      <Info size={12} className="text-secondary" />
                    </Tooltip>
                  </span>
                </Label>
              ),
              placeholder: 'Enter the number of retries',
            },
          },
        ]}
        form={form}
      />
    </div>
  ),
)
